import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import VODFooter from '../components/VODFooter';

const VODPrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Privacy Policy | LTV VOD - Video Streaming Platform</title>
        <meta name="description" content="Privacy Policy for LTV VOD streaming platform. Learn how we collect, use, and protect your personal data when using our video-on-demand services." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/vod/privacy-policy" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          <Link to="/vod" className="flex items-center gap-2">
            <img src="/ltv-icon.png" alt="LTV VOD" className="h-8 w-8 brightness-0 invert" />
            <span className="font-bold">LTV VOD</span>
          </Link>
        </div>
      </header>
      
      {/* Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-gray-400 mb-2">LTV VOD - Video On Demand Platform</p>
          <p className="text-gray-500 mb-8">Last updated: March 2026</p>
          
          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="mb-4">
                Welcome to LTV VOD ("we," "our," or "us"), operated by LTV Production LTD. We are committed to protecting your privacy and ensuring the security of your personal information when you use our video-on-demand streaming platform at ltvproduction.com.
              </p>
              <p>
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, create an account, stream videos, make purchases, or interact with our services.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-white mb-3">2.1 Account Information</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Name and email address when you create an account</li>
                <li>Profile picture (if provided via Google OAuth)</li>
                <li>Account preferences and settings</li>
                <li>Password (stored securely using encryption)</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3">2.2 Video Viewing Data</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Videos watched and watch history</li>
                <li>Viewing duration and completion rates</li>
                <li>Video quality preferences</li>
                <li>Reels/short-form content interactions</li>
                <li>Likes, saves, and watchlist additions</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3">2.3 Payment Information</h3>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Payment method details (processed securely via Stripe)</li>
                <li>Subscription history and billing information</li>
                <li>Purchase records for individual videos</li>
                <li>We do NOT store your full credit card number</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3">2.4 Technical Data</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address and approximate location</li>
                <li>Browser type, version, and settings</li>
                <li>Device information (type, operating system)</li>
                <li>Streaming quality and bandwidth metrics</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Provide Streaming Services:</strong> Deliver video content, manage your account, and process payments</li>
                <li><strong>Personalize Your Experience:</strong> Recommend videos based on viewing history and preferences</li>
                <li><strong>Improve Our Platform:</strong> Analyze usage patterns to enhance features and content</li>
                <li><strong>Process Transactions:</strong> Handle subscriptions, purchases, and refunds</li>
                <li><strong>Communicate With You:</strong> Send service updates, new content alerts, and promotional offers (with consent)</li>
                <li><strong>Ensure Security:</strong> Detect fraud, prevent abuse, and protect our users</li>
                <li><strong>Comply With Legal Obligations:</strong> Meet regulatory requirements and respond to legal requests</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Video Analytics & Recommendations</h2>
              <p className="mb-4">
                To provide personalized content recommendations, we analyze your viewing behavior including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Which videos you watch and for how long</li>
                <li>Content categories you prefer</li>
                <li>Creators you follow or subscribe to</li>
                <li>Time of day viewing patterns</li>
              </ul>
              <p>
                You can control recommendation settings in your account preferences and clear your watch history at any time.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Third-Party Services</h2>
              <p className="mb-4">We use the following third-party services:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cloudflare Stream:</strong> Video hosting and delivery</li>
                <li><strong>Stripe:</strong> Secure payment processing</li>
                <li><strong>Google OAuth:</strong> Account authentication</li>
                <li><strong>Google Analytics:</strong> Website usage analysis</li>
                <li><strong>Google AdSense/Ad Manager:</strong> Advertising (for free-tier users)</li>
              </ul>
              <p className="mt-4">
                Each third-party service has its own privacy policy. We encourage you to review their policies.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Advertising</h2>
              <p className="mb-4">
                Our free tier may display advertisements powered by Google AdSense and Google Ad Manager. These services may:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Use cookies to serve ads based on your interests</li>
                <li>Track ad performance and user interactions</li>
                <li>Collect device and browsing information</li>
              </ul>
              <p>
                Premium subscribers receive an ad-free experience. You can opt out of personalized ads at{' '}
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Google Ads Settings
                </a>.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Data Sharing</h2>
              <p className="mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Companies that help operate our platform (hosting, payments, analytics)</li>
                <li><strong>Content Creators:</strong> Aggregated, anonymized viewing statistics for their content</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect rights</li>
                <li><strong>Business Transfers:</strong> In connection with mergers or acquisitions</li>
              </ul>
              <p className="mt-4 font-semibold">We never sell your personal information to third parties.</p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. GDPR Compliance (EU Users)</h2>
              <p className="mb-4">If you are located in the European Union, you have additional rights under GDPR:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate personal data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Restriction:</strong> Limit how we process your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, contact us at{' '}
                <a href="mailto:privacy@ltvproduction.com" className="text-purple-400 hover:underline">
                  privacy@ltvproduction.com
                </a>
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Data Retention</h2>
              <p className="mb-4">We retain your data for different periods depending on the type:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Data:</strong> Until you delete your account</li>
                <li><strong>Watch History:</strong> 2 years (or until manually cleared)</li>
                <li><strong>Payment Records:</strong> 7 years (for legal/tax compliance)</li>
                <li><strong>Technical Logs:</strong> 90 days</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Data Security</h2>
              <p className="mb-4">We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>SSL/TLS encryption for all data transmission</li>
                <li>Encrypted storage for sensitive information</li>
                <li>Regular security audits and vulnerability testing</li>
                <li>Access controls and authentication requirements</li>
              </ul>
              <p className="mt-4">
                However, no system is 100% secure. We encourage you to use strong passwords and protect your account credentials.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Children's Privacy</h2>
              <p>
                LTV VOD is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Geographic Restrictions</h2>
              <p>
                Some content on LTV VOD may be subject to geographic licensing restrictions. We may use your IP address to determine your location and enforce these restrictions.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. We will notify you of material changes by posting the new policy on this page and updating the "Last updated" date. For significant changes, we may also send you an email notification.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Contact Us</h2>
              <p className="mb-4">For privacy-related inquiries, contact us at:</p>
              <div className="bg-gray-900/50 rounded-lg p-6 space-y-2">
                <p><strong>LTV Production LTD</strong></p>
                <p>Email: <a href="mailto:privacy@ltvproduction.com" className="text-purple-400 hover:underline">privacy@ltvproduction.com</a></p>
                <p>General Support: <a href="mailto:support@ltvproduction.com" className="text-purple-400 hover:underline">support@ltvproduction.com</a></p>
                <p>Location: London, United Kingdom</p>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <VODFooter />
    </div>
  );
};

export default VODPrivacyPolicy;
